import { create } from 'zustand'
import { dashboardApi } from '@/api/dashboard'
import { getErrorMessage } from '@/utils/error'
import type {
  DashboardLayoutData,
  DashboardRole,
  LayoutItem,
  WidgetKey,
  WidgetsCatalog,
} from '@/types/dashboard'

type WidgetPayload = Record<string, unknown>

interface WidgetState {
  data: WidgetPayload | null
  loading: boolean
  error: string | null
}

interface DashboardState {
  role: DashboardRole | null
  catalog: WidgetsCatalog | null
  layout: DashboardLayoutData | null
  widgets: Partial<Record<WidgetKey, WidgetState>>
  editMode: boolean
  /** Черновик раскладки в режиме редактирования (включая скрытые) */
  draftItems: LayoutItem[] | null
  isBootstrapping: boolean
  isSavingLayout: boolean
  error: string | null
  bootstrap: (role: DashboardRole) => Promise<void>
  refetchWidget: (key: WidgetKey) => Promise<void>
  enterEdit: () => void
  cancelEdit: () => void
  setDraftItems: (items: LayoutItem[]) => void
  toggleHidden: (key: WidgetKey) => void
  saveLayout: () => Promise<void>
  resetLayout: () => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  role: null as DashboardRole | null,
  catalog: null as WidgetsCatalog | null,
  layout: null as DashboardLayoutData | null,
  widgets: {} as Partial<Record<WidgetKey, WidgetState>>,
  editMode: false,
  draftItems: null as LayoutItem[] | null,
  isBootstrapping: false,
  isSavingLayout: false,
  error: null as string | null,
}

/** Монотонный счётчик: устаревшие ответы bootstrap игнорируются при смене роли. */
let bootstrapSeq = 0

function visibleKeys(layout: DashboardLayoutData | null): WidgetKey[] {
  return (layout?.items ?? []).filter((item) => !item.hidden).map((item) => item.widget_key)
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  ...initialState,

  bootstrap: async (role) => {
    const seq = ++bootstrapSeq
    set({ ...initialState, role, isBootstrapping: true })
    try {
      const [catalog, layout] = await Promise.all([
        dashboardApi.widgets(role),
        dashboardApi.layout(role),
      ])

      if (seq !== bootstrapSeq) return

      const loadingWidgets: Partial<Record<WidgetKey, WidgetState>> = {}
      for (const key of visibleKeys(layout)) {
        loadingWidgets[key] = { data: null, loading: true, error: null }
      }
      set({ catalog, layout, widgets: loadingWidgets })

      const aggregate = await dashboardApi.aggregate(role)

      if (seq !== bootstrapSeq) return

      const widgets: Partial<Record<WidgetKey, WidgetState>> = {}
      for (const key of visibleKeys(layout)) {
        const payload = aggregate.widgets[key]
        widgets[key] = payload
          ? { data: payload, loading: false, error: null }
          : { data: null, loading: false, error: null }
      }
      set({ widgets, isBootstrapping: false })
    } catch (error) {
      if (seq !== bootstrapSeq) return
      set({ error: getErrorMessage(error), isBootstrapping: false })
    }
  },

  refetchWidget: async (key) => {
    const { role, layout } = get()
    if (!role) return
    // Realtime-push может прийти для виджета другой роли (канал общий) — игнорируем
    if (!layout?.items.some((item) => item.widget_key === key)) return

    set((state) => ({
      widgets: {
        ...state.widgets,
        [key]: { data: state.widgets[key]?.data ?? null, loading: true, error: null },
      },
    }))
    try {
      const aggregate = await dashboardApi.aggregate(role, [key])
      set((state) => ({
        widgets: {
          ...state.widgets,
          [key]: { data: aggregate.widgets[key] ?? null, loading: false, error: null },
        },
      }))
    } catch (error) {
      set((state) => ({
        widgets: {
          ...state.widgets,
          [key]: {
            data: state.widgets[key]?.data ?? null,
            loading: false,
            error: getErrorMessage(error),
          },
        },
      }))
    }
  },

  enterEdit: () => {
    const { layout } = get()
    if (!layout) return
    set({ editMode: true, draftItems: layout.items.map((item) => ({ ...item })) })
  },

  cancelEdit: () => {
    set({ editMode: false, draftItems: null })
  },

  setDraftItems: (items) => {
    set({ draftItems: items })
  },

  toggleHidden: (key) => {
    set((state) => ({
      draftItems: (state.draftItems ?? []).map((item) =>
        item.widget_key === key ? { ...item, hidden: !item.hidden } : item,
      ),
    }))
  },

  saveLayout: async () => {
    const { role, draftItems, widgets } = get()
    if (!role || !draftItems) return

    set({ isSavingLayout: true })
    try {
      const layout = await dashboardApi.saveLayout(role, draftItems)
      set({ layout, editMode: false, draftItems: null, isSavingLayout: false })

      // Данные для виджетов, ставших видимыми и ещё не загруженных
      const missing = visibleKeys(layout).filter((key) => !widgets[key]?.data)
      await Promise.all(missing.map((key) => get().refetchWidget(key)))
    } catch (error) {
      set({ error: getErrorMessage(error), isSavingLayout: false })
    }
  },

  resetLayout: async () => {
    const { role } = get()
    if (!role) return

    set({ isSavingLayout: true })
    try {
      const layout = await dashboardApi.resetLayout(role)
      set({ layout, editMode: false, draftItems: null, isSavingLayout: false })

      const { widgets } = get()
      const missing = visibleKeys(layout).filter((key) => !widgets[key]?.data)
      await Promise.all(missing.map((key) => get().refetchWidget(key)))
    } catch (error) {
      set({ error: getErrorMessage(error), isSavingLayout: false })
    }
  },

  clearError: () => set({ error: null }),

  reset: () => {
    bootstrapSeq += 1
    set({ ...initialState })
  },
}))
